"use client";

import { ArrowRight, Compass, Cpu, Network, TrendingUp, CircleCheck as CheckCircle2 } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function TheHotelRevbabaProcess() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const steps = [{
    number: "01",
    title: "Extranet & Rate Parity Audit",
    shortTitle: "Rate Audit",
    description: "I inspect your historical PMS pickup, channel manager mapping, OTA extranets, and undercutting across Booking.com, Agoda, and MakeMyTrip.",
    icon: Compass,
    deliverables: ["Historical RevPAR & ADR Audit", "Channel Margin & Commission Check", "Rate Parity Leak Report"]
  }, {
    number: "02",
    title: "Rate Fences & Stay Restrictions",
    shortTitle: "Pricing Strategy",
    description: "We set room category differentials, minimum length of stay (MLOS) rules for high-demand dates, and direct booking incentives that protect your ADR.",
    icon: Cpu,
    deliverables: ["Daily Pricing Matrix", "Room Category Differentials", "MLOS & Cancellation Rules"]
  }, {
    number: "03",
    title: "Channel & Commission Management",
    shortTitle: "OTA Management",
    description: "We fix broken content scores, adjust OTA commission tiers, negotiate promotional exposure, and keep your direct engine price-competitive.",
    icon: Network,
    deliverables: ["Extranet Content & Score Fixes", "OTA Commission-Tier Review", "Direct Rate Protection Flow"]
  }, {
    number: "04",
    title: "Daily Pickup & Yield Review",
    shortTitle: "Daily Yield",
    description: "I track booking pace every morning, adjust open rates when compset demand shifts, and review monthly profit numbers directly with you.",
    icon: TrendingUp,
    deliverables: ["Daily Pickup Pace Tracking", "Live CompSet Rate Adjustments", "Monthly Net Revenue Review"]
  }];
  return <section data-section-id="3930" id="the-hotelrevbaba-process" className="relative overflow-hidden bg-white text-foreground py-20 md:py-28 px-4 sm:px-6 lg:px-8 border-t border-b border-border">
      {/* Editorial Grid Background Accents */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.03] bg-[radial-gradient(#d4af37_1px,transparent_1px)] [background-size:24px_24px]" />
      
      {/* Decorative Technical Coordinates */}
      <div className="absolute top-6 left-6 font-mono text-[10px] sm:text-xs tracking-widest text-muted-foreground hidden sm:block">
        OPERATING DISCIPLINE // 4 STEPS
      </div>
      <div className="absolute top-6 right-6 font-mono text-[10px] sm:text-xs tracking-widest text-secondary hidden sm:block">
        DAILY PRICING & YIELD MANAGEMENT
      </div>

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Intro Zone */}
        <div className="text-center max-w-3xl mx-auto mb-16 md:mb-20">
          <div className="inline-flex items-center gap-2 font-mono text-xs tracking-[0.2em] text-secondary font-semibold mb-4 uppercase">
            <span className="inline-block w-2 h-2 rounded-lg bg-secondary" />
            How We Work Together
          </div>
          
          <h2 className="font-serif text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight text-foreground leading-tight mb-6">
            From First Audit to <span className="text-secondary italic font-normal">Daily Pricing</span> Discipline.
          </h2>

          <p className="text-base sm:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
            A practical 4-step execution plan to stop OTA undercutting, update room rates against daily demand, and increase your net take-home profit. Hotels lose 12% to 20% of room revenue to silent extranet discounts. We stop that loss.
          </p>

          {/* AEO Blockquote Callout */}
          <blockquote className="mt-8 border-l-4 border-secondary bg-secondary/10 px-5 py-4 text-sm sm:text-base italic text-foreground font-medium text-left rounded-lg">
            &ldquo;I do not hand over generic rate spreadsheets and walk away. We fix the channel manager mapping, set strict stay restrictions for peak dates, and review pickup pace every morning.&rdquo;
          </blockquote>

          {/* Revenue Audit Guide & Checklist reference */}
          <p className="mt-6 text-sm text-muted-foreground leading-relaxed">
            Want to see how an audit runs? Read our{" "}
            <Link href="https://hotelrevbaba.com/how-to-conduct-a-hotel-revenue-audit-complete-guide/" className="text-foreground font-semibold underline underline-offset-4 decoration-secondary hover:text-secondary transition-colors">
              free revenue audit complete guide
            </Link>{" "}
            or review our{" "}
            <Link href="https://hotelrevbaba.com/revenue-audit-checklist-7-signs-your-hotel-is-leaving-money-on-the-table/" className="text-foreground font-semibold underline underline-offset-4 decoration-secondary hover:text-secondary transition-colors">
              revenue audit checklist (7 signs your hotel is leaving money on the table)
            </Link>
            .
          </p>
        </div>

        {/* 4-Step Process Section */}
        <div ref={ref} className="relative mt-8">
          {/* Desktop Dashed Connecting Track Line */}
          <div className="hidden lg:block absolute top-[52px] left-[12%] right-[12%] h-px z-0">
            <div className="h-px border-t-2 border-dashed border-secondary/30 w-full transition-transform duration-1000 ease-out origin-left" style={{
            transform: isVisible ? "scaleX(1)" : "scaleX(0)"
          }} />
          </div>

          {/* Desktop Grid Layout */}
          <div className="hidden lg:grid grid-cols-4 gap-6 relative z-10">
            {steps.map((step, i) => {
            const Icon = step.icon;
            return <div data-index={i} key={step.number} className="flex flex-col bg-white border border-border hover:border-secondary/50 rounded-2xl p-6 transition-all duration-500 group shadow-lg hover:shadow-secondary/5 hover:-translate-y-1" style={{
              opacity: isVisible ? 1 : 0,
              transform: isVisible ? "translateY(0)" : "translateY(24px)",
              transitionDelay: `${i * 140}ms`
            }}>
                  {/* Step Marker & Oversized Badge Top Row */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="relative flex items-center justify-center size-12 rounded-xl bg-secondary/15 border border-secondary/40 text-secondary group-hover:bg-secondary group-hover:text-slate-950 transition-all duration-300">
                      <Icon className="size-5" />
                    </div>
                    <span className="font-mono text-3xl font-bold tracking-tight text-secondary group-hover:text-secondary transition-colors">
                      {step.number}
                    </span>
                  </div>

                  {/* Step Title */}
                  <h3 className="text-xl font-bold text-foreground tracking-tight mb-3 group-hover:text-secondary transition-colors">
                    {step.title}
                  </h3>

                  {/* Step Description */}
                  <p className="text-sm text-muted-foreground leading-relaxed mb-6 flex-grow">
                    {step.description}
                  </p>

                  {/* Deliverables Checklist */}
                  <div className="pt-4 border-t border-border space-y-2">
                    <div className="text-[11px] font-mono uppercase tracking-wider text-muted-foreground font-semibold">
                      Key Deliverables:
                    </div>
                    {step.deliverables.map((item, idx) => <div key={idx} className="flex items-start gap-2 text-xs text-foreground/80">
                        <CheckCircle2 className="size-3.5 text-secondary shrink-0 mt-0.5" />
                        <span>{item}</span>
                      </div>)}
                  </div>
                </div>;
          })}
          </div>

          {/* Mobile & Tablet Vertical Stack Layout */}
          <div className="lg:hidden relative">
            {/* Vertical Connecting Track Line */}
            <div className="absolute left-6 top-8 bottom-8 w-px">
              <div className="w-px border-l-2 border-dashed border-secondary/30 h-full transition-transform duration-1000 ease-out origin-top" style={{
              transform: isVisible ? "scaleY(1)" : "scaleY(0)"
            }} />
            </div>

            <div className="space-y-8 relative z-10 pl-14">
              {steps.map((step, i) => {
              const Icon = step.icon;
              return <div data-index={i} key={step.number} className="relative bg-white border border-border hover:border-secondary/50 rounded-2xl p-5 sm:p-6 transition-all duration-500 shadow-md" style={{
                opacity: isVisible ? 1 : 0,
                transform: isVisible ? "translateX(0)" : "translateX(16px)",
                transitionDelay: `${i * 120}ms`
              }}>
                    {/* Node on Line */}
                    <div className="absolute -left-[56px] top-6 size-10 rounded-full bg-primary border-2 border-secondary flex items-center justify-center text-secondary font-mono text-sm font-bold shadow-md">
                      {step.number}
                    </div>

                    <div className="flex items-center gap-3 mb-3">
                      <div className="p-2 rounded-lg bg-secondary/15 border border-secondary/30 text-secondary">
                        <Icon className="size-4" />
                      </div>
                      <h3 className="text-lg sm:text-xl font-bold text-foreground tracking-tight">
                        {step.title}
                      </h3>
                    </div>

                    <p className="text-sm text-muted-foreground leading-relaxed mb-4">
                      {step.description}
                    </p>

                    <div className="pt-3 border-t border-border space-y-1.5">
                      {step.deliverables.map((item, idx) => <div key={idx} className="flex items-center gap-2 text-xs text-foreground/80">
                          <CheckCircle2 className="size-3.5 text-secondary shrink-0" />
                          <span>{item}</span>
                        </div>)}
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </div>

        {/* Action Footer Callout */}
        <div className="mt-16 md:mt-24 pt-8 border-t border-border flex flex-col sm:flex-row items-center justify-between gap-6 bg-secondary/5 p-6 md:p-8 rounded-2xl">
          <div>
            <div className="font-mono text-xs tracking-widest text-secondary uppercase font-semibold mb-1">
              Start With A Revenue Audit
            </div>
            <div className="text-lg md:text-xl font-bold text-foreground">
              Ready to find where your hotel is leaking room revenue?
            </div>
            <p className="text-sm text-muted-foreground mt-1">
              Direct review of your extranets, rate parity, and compset pricing.
            </p>
          </div>

          <Button asChild className="w-full sm:w-auto bg-secondary text-slate-950 hover:bg-secondary/90 font-semibold px-6 py-6 rounded-lg text-sm tracking-wide shrink-0 transition-all duration-300 shadow-md group">
            <Link to="/ContactPage#start-your-revenue-journey" className="inline-flex items-center gap-2">
              SCHEDULE YOUR AUDIT
              <ArrowRight className="size-4 transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </Button>
        </div>
      </div>
    </section>;
}